import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@tubo/ui/components/ui/tabs";
import { useSecondaryNavActions, useSecondaryNavTitle } from "@client/components/secondary-nav";
import {
  ListFilterSelect,
  ListPageHeaderActions,
  ListSearchInput,
} from "@client/components/common/list-page-controls";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { NS_CONTEXT_ALL } from "@tubo/semantic-concepts";
import {
  useListClassificationFields,
  useListGovernanceSuggestions,
  useListUnifiedGovernanceSuggestions,
} from "@tubo/api-models/hooks/classification-governance-api.hooks";
import {
  ClassificationSuggestionsTab,
} from "@client/components/semantic-platform/data-governance/suggestions/classification-suggestions-tab";
import { MappingSuggestionsTab } from "@client/components/semantic-platform/data-governance/suggestions/mapping-suggestions-tab";
import {
  PAGE_SIZE,
  SOURCE_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
} from "@client/components/semantic-platform/data-governance/suggestions/suggestion-shared";

export const Route = createFileRoute("/_app/semantic-platform/tag-center/suggestions/")({
  // 领域上下文入 URL（经 字段/词表/概念 锚点派生领域过滤）；field=使用分析钻取带来的字段预过滤
  validateSearch: (search: Record<string, unknown>): { ns?: string; field?: string } => {
    return {
      ...(typeof search.ns === "string" && search.ns ? { ns: search.ns } : {}),
      ...(typeof search.field === "string" && search.field ? { field: search.field } : {}),
    };
  },
  component: TagSuggestionsPage,
});

type SuggestionTab = "mapping" | "classification";

function TagSuggestionsPage() {
  const navigate = useNavigate();
  const { field } = Route.useSearch();
  const [tab, setTab] = useState<SuggestionTab>("classification");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [mappingPage, setMappingPage] = useState(1);
  const [classificationPage, setClassificationPage] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const isComposing = useRef(false);
  const handleSearchConfirm = useCallback((value: string) => {
    setSearch(value);
    setMappingPage(1);
    setClassificationPage(1);
  }, []);
  // 领域上下文（经 字段/词表/概念 锚点派生过滤）
  const { nsFilter } = useNsContext();
  const { data: unifiedSuggestions = [], isLoading: unifiedLoading } = useListUnifiedGovernanceSuggestions(nsFilter !== NS_CONTEXT_ALL ? { ns: nsFilter } : undefined, { query: {} });
  const { data = [], isLoading } = useListGovernanceSuggestions(
    { status: "pending", ...(nsFilter !== NS_CONTEXT_ALL ? { ns: nsFilter } : {}) },
    { query: {} },
  );
  // 字段过滤 chip 的显示名（使用分析钻取传 code，列表一次轻查解析名称）
  const { data: fieldsForName = [] } = useListClassificationFields(undefined, { query: { enabled: Boolean(field) } });
  const fieldLabel = useMemo(
    () => (field ? (fieldsForName.find((f) => f.code === field)?.name ?? field) : ""),
    [field, fieldsForName],
  );


  const keyword = search.trim().toLowerCase();
  const mappingSuggestions = useMemo(
    () => unifiedSuggestions.filter((suggestion) => {
      if (suggestion.kind !== "columnMapping") return false;
      if (!keyword) return true;
      const searchableText = [suggestion.rawText, suggestion.targetRef, suggestion.sourceModule, suggestion.sourceContext]
        .filter(Boolean).join(" ").toLowerCase();
      return searchableText.includes(keyword);
    }),
    [unifiedSuggestions, keyword],
  );
  const filteredSuggestions = useMemo(
    () => data.filter((suggestion) => {
      // URL field 参数预过滤（使用分析钻取带入；数据已含 fieldCode，客户端过滤即可）
      if (field && suggestion.fieldCode !== field) return false;
      const searchableText = [
        suggestion.rawText,
        suggestion.normalizedText,
        suggestion.fieldCode,
        suggestion.vocabularyCode,
        suggestion.subjectId,
      ].filter(Boolean).join(" ").toLowerCase();
      const matchesSearch = !keyword || searchableText.includes(keyword);
      const matchesType = typeFilter === "all" || suggestion.suggestionType === typeFilter;
      const matchesSource = sourceFilter === "all" || suggestion.source === sourceFilter;
      return matchesSearch && matchesType && matchesSource;
    }),
    [data, keyword, sourceFilter, typeFilter, field],
  );

  const [mappingPageSize, setMappingPageSize] = useState(PAGE_SIZE);
  const [classificationPageSize, setClassificationPageSize] = useState(PAGE_SIZE);
  const mappingPageCount = Math.max(1, Math.ceil(mappingSuggestions.length / mappingPageSize));
  const currentMappingPage = Math.min(mappingPage, mappingPageCount);
  const visibleMapping = mappingSuggestions.slice((currentMappingPage - 1) * mappingPageSize, currentMappingPage * mappingPageSize);
  const classificationPageCount = Math.max(1, Math.ceil(filteredSuggestions.length / classificationPageSize));
  const currentClassificationPage = Math.min(classificationPage, classificationPageCount);
  const visibleSuggestions = filteredSuggestions.slice((currentClassificationPage - 1) * classificationPageSize, currentClassificationPage * classificationPageSize);

  useSecondaryNavTitle(useMemo(() => <span className="text-sm font-semibold">建议审核</span>, []));
  useSecondaryNavActions(useMemo(() => (
    <ListPageHeaderActions>
      {field && tab === "classification" && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          title={`仅显示字段「${fieldLabel}」的待审建议`}
          onClick={() => void navigate({
            to: "/semantic-platform/tag-center/suggestions",
            search: (prev) => {
              const { field: _field, ...rest } = prev;
              return rest;
            },
          })}
        >
          字段：{fieldLabel}
          <X className="size-3" />
        </Button>
      )}
      {tab === "classification" && (
        <>
          <ListFilterSelect
            items={TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onValueChange={(value) => { setTypeFilter(value ?? "all"); setClassificationPage(1); }}
          />
          <ListFilterSelect
            items={SOURCE_FILTER_OPTIONS}
            value={sourceFilter}
            onValueChange={(value) => { setSourceFilter(value ?? "all"); setClassificationPage(1); }}
          />
        </>
      )}
      <ListSearchInput
        placeholder="搜索建议内容或目标"
        className="w-56"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          if (isComposing.current) return;
          handleSearchConfirm(event.target.value);
        }}
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={(event) => {
          isComposing.current = false;
          handleSearchConfirm(event.currentTarget.value);
        }}
        onBlur={(event) => {
          if (!isComposing.current) handleSearchConfirm(event.currentTarget.value);
        }}
      />
    </ListPageHeaderActions>
  ), [inputValue, sourceFilter, tab, typeFilter, handleSearchConfirm, field, fieldLabel, navigate]));

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value === "classification" ? "classification" : "mapping")}
      className="h-full min-h-0 min-w-0 gap-0"
    >
      <div className="flex shrink-0 items-center border-b border-border-muted px-2">
        <TabsList variant="line" className="justify-start">
          <TabsTrigger value="classification">标签打标建议<Badge variant="secondary" className="tabular-nums">{filteredSuggestions.length}</Badge></TabsTrigger>
          <TabsTrigger value="mapping">语义映射建议<Badge variant="secondary" className="tabular-nums">{mappingSuggestions.length}</Badge></TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="mapping" className="flex min-h-0 flex-1 flex-col">
        <MappingSuggestionsTab
          data={visibleMapping}
          page={currentMappingPage}
          total={mappingSuggestions.length}
          isLoading={unifiedLoading}
          onPageChange={setMappingPage}
          pageSize={mappingPageSize}
          onPageSizeChange={(size) => {
            setMappingPageSize(size);
            setMappingPage(1);
          }}
        />
      </TabsContent>
      <TabsContent value="classification" className="flex min-h-0 flex-1 flex-col">
        <ClassificationSuggestionsTab
          data={visibleSuggestions}
          page={currentClassificationPage}
          total={filteredSuggestions.length}
          isLoading={isLoading}
          nsFilter={nsFilter}
          onPageChange={setClassificationPage}
          pageSize={classificationPageSize}
          onPageSizeChange={(size) => {
            setClassificationPageSize(size);
            setClassificationPage(1);
          }}
        />
      </TabsContent>
    </Tabs>
  );
}
